import type { MonsterSize } from "../types/entity.js";

/**
 * Small-pack co-occupancy rules (E-pack, the EoB2 "skeleton pack" feature).
 *
 * A cell holds ONE medium-or-larger enemy, or up to `PACK_MAX` smalls.
 * Sizes never mix in a cell. The party's own cell is unaffected — players
 * never share a cell with enemies (CollisionDetector blocks entry both
 * ways).
 *
 * Sub-cell placement mirrors the party's 2×2 formation: pack members
 * stand in the four corners of their cell, offset on both axes so a pack
 * reads as a group — some abreast, some behind — from any viewing
 * direction. Slot order fills the diagonal first so a 2-pack is always
 * separated in BOTH width and depth on screen (two members sharing only
 * a row or only a column would merge into one blob when viewed along
 * that axis).
 */

export const PACK_MAX = 4;

/** Sub-cell corner offset magnitude, in world units (cell is 1×1). */
export const PACK_SPREAD = 0.22;

/**
 * Corner offsets by slot index, diagonal-first:
 * slot 0 = near-left, slot 1 = far-right (completes the diagonal),
 * slot 2 = near-right, slot 3 = far-left.
 */
const SLOT_OFFSETS: ReadonlyArray<{ dx: number; dz: number }> = [
  { dx: -PACK_SPREAD, dz: -PACK_SPREAD },
  { dx: PACK_SPREAD, dz: PACK_SPREAD },
  { dx: PACK_SPREAD, dz: -PACK_SPREAD },
  { dx: -PACK_SPREAD, dz: PACK_SPREAD },
];

/**
 * World-space offset from cell center for a pack member.
 * A lone occupant stands dead center regardless of size.
 */
export function subCellOffset(slot: number, packCount: number): { dx: number; dz: number } {
  if (packCount <= 1) return { dx: 0, dz: 0 };
  return SLOT_OFFSETS[slot] ?? { dx: 0, dz: 0 };
}

/** How many enemies of `size` may share one cell. */
export function maxOccupantsFor(size: MonsterSize): number {
  return size === "small" ? PACK_MAX : 1;
}

/**
 * May an enemy of `moverSize` enter a cell already holding
 * `occupantSizes`? Unknown occupants (NPCs, anything without an enemy
 * record) must be passed as `null` — they always block.
 */
export function canJoinCell(
  moverSize: MonsterSize,
  occupantSizes: ReadonlyArray<MonsterSize | null>,
): boolean {
  if (occupantSizes.length === 0) return true;
  if (moverSize !== "small") return false;
  if (occupantSizes.length >= PACK_MAX) return false;
  return occupantSizes.every((s) => s === "small");
}
